import React, { useState, useEffect } from "react";
import { Spin, Button, Upload, Tag, Tabs } from "antd";
import {
  UploadOutlined,
  FileSyncOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import * as recruitmentApi from "../../../../../core/data/redux/services/recruitmentApi";
import { notify } from "../../../../../services/toast.config";

interface DocumentRequest {
  request_id: number;
  document_type: string;
  document_name: string;
  description: string;
  is_mandatory: boolean;
  status: string;
  requested_at: string;
  deadline?: string | null;
  days_remaining?: number | null;
  job_title?: string;
}

interface DocumentsTabProps {
  primaryColor?: string;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  primaryColor = "#4f46e5",
}) => {
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState<DocumentRequest[]>([]);
  const [submittedRequests, setSubmittedRequests] = useState<DocumentRequest[]>(
    [],
  );
  const [uploadingId, setUploadingId] = useState<number | null>(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      
      const pendingData = await recruitmentApi.getCandidatePendingRequests(0);
      const pending =
        pendingData?.pending_requests ?? pendingData?.requests ?? [];
      setPendingRequests(Array.isArray(pending) ? pending : []);

      
      const submittedData = await (
        recruitmentApi as any
      ).getCandidateSubmittedRequests?.();
      const submitted =
        submittedData?.pending_requests ?? submittedData?.requests ?? [];
      setSubmittedRequests(Array.isArray(submitted) ? submitted : []);
    } catch (err) {
      console.error("[DocumentsTab] fetchRequests error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  
  const handleUpload = async (
    file: File,
    requestId: number,
    documentType: string,
  ): Promise<false> => {
    setUploadingId(requestId);
    try {
      
      const formData = new FormData();
      formData.append("document", file);
      formData.append("document_type", documentType);
      formData.append("is_primary", "false");

      const uploadResult = await (recruitmentApi as any).candidateUploadDocument(
        formData,
      );

      if (!uploadResult?.file_path) {
        throw new Error("File upload did not return a file_path");
      }

      
      await (recruitmentApi as any).submitRequestedDocument({
        request_id: requestId,
        file_name: uploadResult.file_name ?? file.name,
        file_path: uploadResult.file_path,
        file_size: uploadResult.file_size ?? file.size,
        mime_type: file.type,
        comments: "",
      });

      notify.success("Document uploaded and submitted successfully!");
      fetchRequests();
    } catch (err: any) {
      notify.error(
        err?.data?.error?.message ??
        err?.data?.message ??
        err?.message ??
        "Failed to upload document",
      );
    } finally {
      setUploadingId(null);
    }
    return false; 
  };

  

  const RequestCard: React.FC<{
    req: DocumentRequest;
    showUpload?: boolean;
  }> = ({ req, showUpload = false }) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        padding: 20,
        border: `1px solid ${req.status === "submitted" ? "#d1fae5" : "#e5e7eb"}`,
        borderRadius: 12,
        background: req.status === "submitted" ? "#f0fdf4" : "#fff",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        gap: 12,
      }}
    >
      
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 4,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 15, color: "#1f2937" }}>
            {req.document_name}
          </span>
          {req.is_mandatory && <Tag color="error">Mandatory</Tag>}
          {req.status === "pending" ? (
            <Tag color="warning" icon={<ClockCircleOutlined />}>
              Pending
            </Tag>
          ) : (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              Submitted
            </Tag>
          )}
        </div>

        {req.job_title && (
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
            <i className="ti ti-briefcase" style={{ marginRight: 4 }} />
            {req.job_title}
          </div>
        )}

        <div style={{ fontSize: 13, color: "#6b7280" }}>
          {req.description ||
            `Please provide your ${req.document_type} document.`}
        </div>

        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 12, color: "#9ca3af" }}>
            Requested: {new Date(req.requested_at).toLocaleDateString()}
          </span>
          {req.deadline && (
            <span
              style={{
                fontSize: 12,
                color:
                  (req.days_remaining ?? 99) <= 2 ? "#ef4444" : "#f59e0b",
                fontWeight: 500,
              }}
            >
              <i
                className="ti ti-calendar-due"
                style={{ marginRight: 3 }}
              />
              Due: {new Date(req.deadline).toLocaleDateString()}
              {req.days_remaining != null && ` (${req.days_remaining}d left)`}
            </span>
          )}
        </div>
      </div>

      
      <div style={{ flexShrink: 0 }}>
        {showUpload && req.status === "pending" && (
          <Upload
            beforeUpload={(file) =>
              handleUpload(file, req.request_id, req.document_type)
            }
            showUploadList={false}
            disabled={uploadingId === req.request_id}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          >
            <Button
              icon={<UploadOutlined />}
              loading={uploadingId === req.request_id}
              type="primary"
              style={{ background: primaryColor, borderColor: primaryColor }}
            >
              Upload
            </Button>
          </Upload>
        )}
        {req.status === "submitted" && (
          <Button
            icon={<CheckCircleOutlined />}
            type="default"
            disabled
            style={{
              color: "#10b981",
              borderColor: "#10b981",
              background: "#f0fdf4",
            }}
          >
            Uploaded
          </Button>
        )}
      </div>
    </div>
  );

  const EmptyState: React.FC<{ msg: string }> = ({ msg }) => (
    <div
      style={{
        textAlign: "center",
        padding: "40px 0",
        background: "#f9fafb",
        borderRadius: 12,
        border: "1px dashed #e5e7eb",
      }}
    >
      <CheckCircleOutlined
        style={{ fontSize: 32, color: "#10b981", marginBottom: 16 }}
      />
      <h3 style={{ margin: 0, color: "#374151", fontSize: 16 }}>
        All Caught Up!
      </h3>
      <p style={{ color: "#6b7280", margin: "8px 0 0" }}>{msg}</p>
    </div>
  );

  

  if (loading) {
    return (
      <div
        style={{
          minHeight: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: `${primaryColor}18`,
            color: primaryColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
          }}
        >
          <FileSyncOutlined />
        </div>
        <div>
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              margin: 0,
              color: "#111827",
            }}
          >
            Requested Documents
          </h2>
          <div style={{ fontSize: 14, color: "#6b7280" }}>
            Upload the documents requested by the hiring team.
          </div>
        </div>
      </div>

      
      <Tabs
        defaultActiveKey="pending"
        items={[
          {
            key: "pending",
            label: (
              <span>
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                Pending
                {pendingRequests.length > 0 && (
                  <span
                    style={{
                      marginLeft: 6,
                      background: "#fef3c7",
                      color: "#92400e",
                      borderRadius: 10,
                      fontSize: 11,
                      padding: "1px 7px",
                      fontWeight: 600,
                    }}
                  >
                    {pendingRequests.length}
                  </span>
                )}
              </span>
            ),
            children:
              pendingRequests.length === 0 ? (
                <EmptyState msg="No pending document requests at this time." />
              ) : (
                <div style={{ display: "grid", gap: 14 }}>
                  {pendingRequests.map((req) => (
                    <RequestCard
                      key={req.request_id}
                      req={req}
                      showUpload
                    />
                  ))}
                </div>
              ),
          },
          {
            key: "submitted",
            label: (
              <span>
                <CheckCircleOutlined style={{ marginRight: 6 }} />
                Submitted
                {submittedRequests.length > 0 && (
                  <span
                    style={{
                      marginLeft: 6,
                      background: "#d1fae5",
                      color: "#065f46",
                      borderRadius: 10,
                      fontSize: 11,
                      padding: "1px 7px",
                      fontWeight: 600,
                    }}
                  >
                    {submittedRequests.length}
                  </span>
                )}
              </span>
            ),
            children:
              submittedRequests.length === 0 ? (
                <EmptyState msg="You haven't submitted any documents yet." />
              ) : (
                <div style={{ display: "grid", gap: 14 }}>
                  {submittedRequests.map((req) => (
                    <RequestCard key={req.request_id} req={req} />
                  ))}
                </div>
              ),
          },
        ]}
      />
    </div>
  );
};

export default DocumentsTab;
